import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { Form, Button, FloatingLabel } from 'react-bootstrap';
import { toast } from 'react-toastify';
import '../../Assets/Styles/customFonts.css';
import '../../Components/_Authentication/LoginPage.css';
import logo from '../../Assets/Images/NEXI_RGB.jpg';
import Cookies from 'universal-cookie';
import login from '../../Components/_Authentication/Login';
import { REDIRECT_URL, CLIENT_ID, AUTH_URL, LOGIN_URL, TOKEN_URL, VPN_CHECK_URL, INTERNAL_FEDERATION_URL } from "../../Config/AppConfiguration";
import { useLocation } from 'react-router-dom';

const generateCodeVerifier = () => {
  const array = new Uint8Array(128);
  window.crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(36)).join('').substring(0, 128);
};

const generateCodeChallenge = async (codeVerifier) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

const Home = () => {
  const [isVpnConnected, setIsVpnConnected] = useState(false);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [loginError, setLoginError] = useState('');

  const location = useLocation();
  const formRef = useRef(null);

  // Clear session and cookies function (similar to AppAuthentication)
  const clearAllSessionData = () => {
    const cookies = new Cookies();
    const cookieNameUser = "userId";
    const cookieNameToken = "accessToken";
    const cookieNameCurrentUser = "currentAPIMUser";
    
    // Clear cookies if they exist
    if (cookies.get(cookieNameToken)) {
      cookies.remove(cookieNameToken);
      cookies.remove(cookieNameUser);
      cookies.remove(cookieNameCurrentUser);
    }
    
    // Clear session storage
    sessionStorage.clear();
    
    console.log("Cleared all session data and cookies for fresh login attempt");
  };

  // Handle login with Nexi
  const handleLoginWithNexi = async () => {
    try {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = INTERNAL_FEDERATION_URL;

      const refererInput = document.createElement('input');
      refererInput.type = 'hidden';
      refererInput.name = 'Referer';
      refererInput.value = `${REDIRECT_URL}/home`;
      form.appendChild(refererInput);

      const contentTypeInput = document.createElement('input');
      contentTypeInput.type = 'hidden';
      contentTypeInput.name = 'Content-Type';
      contentTypeInput.value = 'application/json';
      form.appendChild(contentTypeInput);

      document.body.appendChild(form);
      form.submit();

      
      console.log('Form submitted for Login with Nexi!');
    } catch (error) {
      console.error('Error during login with Nexi:', error.message);
      toast.error('Login with Nexi failed. Please try again or contact support.');
    }
  };

  useEffect(() => {
    const checkVpnConnection = async () => {
      //  custom axios instance to avoid global interceptors
      const customAxios = axios.create();
      try {
        const response = await customAxios.get(VPN_CHECK_URL, { headers: { 'Content-Type': 'application/json' }, withCredentials: true });
        if (response.status === 200) {
          setIsVpnConnected(true);
          sessionStorage.setItem('vpnConnected', 'true');
        }
      } catch (error) {
        // Only handle VPN error here, do not trigger global error page
        console.error('Error checking VPN connection:', error.message);
        setIsVpnConnected(false);
        sessionStorage.setItem('vpnConnected', 'false');
      }
    };

    checkVpnConnection();
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const errorCode = params.get('error_code');

    // Handle authentication errors
    if (errorCode) {
      const errorMessage = `Username or password are invalid`;
      setLoginError(errorMessage);
      
      // Clear session data after a brief moment to ensure error renders first
      setTimeout(() => {
        clearAllSessionData();
      }, 50);
      
      setTimeout(() => {
        const newUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
      }, 100);
      
      return;
    }

    let codeVerifierStored = sessionStorage.getItem('codeVerifier');
    let codeChallengeStored = sessionStorage.getItem('codeChallenge');
    let stateStored = sessionStorage.getItem('oauthState');

    const generateAndStorePKCE = async () => {
      codeVerifierStored = generateCodeVerifier();
      sessionStorage.setItem('codeVerifier', codeVerifierStored);
      codeChallengeStored = await generateCodeChallenge(codeVerifierStored);
      sessionStorage.setItem('codeChallenge', codeChallengeStored);
      stateStored = uuidv4();
      sessionStorage.setItem('oauthState', stateStored);
    };

    const redirectToAuthorize = () => {
      const nonce = btoa(Math.random()).slice(0, 24);
      const newAuthUrl = AUTH_URL +
        "&redirect_uri=" + encodeURIComponent(REDIRECT_URL + "/home") +
        "&nonce=" + nonce +
        "&code_challenge=" + codeChallengeStored +
        "&state=" + stateStored;
      //setAuthUrl(newAuthUrl);
      console.log("Auth was called for the first time. Redirecting to:", newAuthUrl);
      window.location.href = newAuthUrl;
    };

    (async () => {
      if (!codeVerifierStored || !codeChallengeStored || !stateStored) {
        await generateAndStorePKCE();
        codeVerifierStored = sessionStorage.getItem('codeVerifier');
        codeChallengeStored = sessionStorage.getItem('codeChallenge');
        stateStored = sessionStorage.getItem('oauthState');
      }
      if (!code && !sessionStorage.getItem('authRedirected')) {
        sessionStorage.setItem('authRedirected', 'true');
        redirectToAuthorize();
      }
      if (code) {
        sessionStorage.removeItem('authRedirected');
      }
    })();
    const cookies = new Cookies();
    const accessToken = cookies.get('accessToken');
    if (accessToken && accessToken.trim() !== '') {
      console.log('Access token found, redirecting to /projects');
      window.location.href = '/projects';
    } else {
      console.log('Access token not found or invalid, staying on login page');
    }
  }, [location.search]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    
    try {
      if (formRef.current) {
        formRef.current.submit();
      }
    } catch (error) {
      console.error('Error during form submission:', error);
      toast.error('Login failed. Please try again.');
      setFormSubmitting(false);
    }
  };

  return (
    <div className="login-page-root">
      <div className="container-fluid" style={{ minHeight: '400px', margin: '0', width: "100%" }}>
        <div className={`main-container${isVpnConnected ? ' vpn-connected' : ''}`}>
          <div className={`login-container${isVpnConnected ? ' vpn-login-container' : ''}`}>
            <img
              src={logo}
              width={69}
              alt="Logo"
              className={`logo${isVpnConnected ? ' vpn-logo' : ''}`}
            />
            {isVpnConnected ? (
              <>
                <p className={`signIn-text${isVpnConnected ? ' vpn-signIn-text' : ''}`}>
                  Sign in with your NexiGroup Account
                </p>
                <Button
                  variant="success"
                  type="button"
                  className={`login-with-nexi-button${isVpnConnected ? ' vpn-login-btn' : ''}`}
                  onClick={handleLoginWithNexi}
                  style={{ width: "100%", marginTop: "0px" }}
                >
                  Login with Nexi
                </Button>
              </>
            ) : (
              <>
                <p className="signIn-text">Sign in for your Nexi Account</p>
                <h2>Welcome!</h2>
                <p>Please enter your username and password.</p>
                <form
                  ref={formRef}
                  action={LOGIN_URL}
                  method="POST"
                  className="login-form"
                  onSubmit={handleSubmit}
                  style={{ width: "100%" }}
                >
                  <FloatingLabel
                    controlId="floatingEmail"
                    label="Enter your email"
                    className="mb-3"
                  >
                    <Form.Control
                      type="email"
                      placeholder="Enter your email"
                      value={username}
                      onChange={(e) => {
                        setUsername(e.target.value);
                        if (loginError) setLoginError('');
                      }}
                      required
                      name="username"
                      disabled={formSubmitting}
                      autoComplete="username"
                    />
                  </FloatingLabel>

                  <a href="/resetPassword" className="forgot-password-link">
                    Forgot Password?
                  </a>

                  <FloatingLabel
                    controlId="floatingPassword"
                    label="Enter your password"
                    className="mb-3"
                  >
                    <Form.Control
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (loginError) setLoginError('');
                      }}
                      required
                      name="password"
                      disabled={formSubmitting}
                      autoComplete="current-password"
                    />
                  </FloatingLabel>

                  {loginError && (
                    <div style={{ 
                      color: '#d32f2f', 
                      fontSize: '14px', 
                      textAlign: 'right',
                      marginBottom: '15px',
                      fontWeight: '400',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: '4px'
                    }}>
                      <span>{loginError}</span>
                      <span style={{ 
                        position: 'relative',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        ▲
                        <span style={{
                          position: 'absolute',
                          top: '2px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontSize: '8px',
                          color: '#d32f2f'
                        }}>!</span>
                      </span>
                    </div>
                  )}

                  <Button variant="primary" type="submit" className="signin-button" disabled={formSubmitting}>
                    SIGN IN
                  </Button>
                </form>
                <p className="new-user-text">
                  New user?{' '}
                  <a href="/joinUs" className="signup-link">
                    Sign up here
                  </a>
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
